import { createContext, useContext, useState, useEffect } from 'react';
import { onAuthStateChanged, signInWithPopup, signOut } from 'firebase/auth';
import { auth, googleProvider } from '../firebase/firebase';

const AuthContext = createContext();

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if demo mode was previously set
    const demoMode = localStorage.getItem('demoMode') === 'true';
    if (demoMode) {
      setIsDemoMode(true);
      setLoading(false);
      return;
    }

    // Listen for auth state changes
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const loginWithGoogle = async () => {
    try {
      await signInWithPopup(auth, googleProvider);
      localStorage.removeItem('demoMode');
      setIsDemoMode(false);
    } catch (error) {
      console.error('Error signing in with Google:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      if (isDemoMode) {
        localStorage.removeItem('demoMode');
        localStorage.removeItem('demoTasks');
        setIsDemoMode(false);
      } else {
        await signOut(auth);
      }
      setUser(null);
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  };

  const startDemoMode = () => {
    localStorage.setItem('demoMode', 'true');
    setIsDemoMode(true);
  };

  const value = {
    user,
    isDemoMode,
    loading,
    loginWithGoogle,
    logout,
    startDemoMode,
    isAuthenticated: !!user || isDemoMode,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
